import {observable, action} from 'mobx';
import {DomainStore} from 'shared/store';
import {types, endpoints} from 'shared/core';
import {MassUpdate, MassUpdateEmployee} from 'stores/mass_updates';
import _ from 'lodash';
import {setupAutosaveDraft} from 'shared/tools';
import {Employee} from 'stores/employees';

class MassUpdateEditorState {
  store = new DomainStore();
  match;
  history;

  @observable errors = {};
  @observable addEmployeeErrors = {};
  @observable massUpdate;
  @observable addEmployeesModalOpen = false;
  @observable publishConfirmationModalOpen = false;
  @observable selectedEmployees = [];
  @observable interactiveAgent = [];
  @observable bulkEditField = null;
  @observable bulkEditValue = null;
  @observable bulkEditModalOpen = false;

  receiveProps({match, history}) {
    this.match = match;
    this.history = history;
  }

  setInteractiveAgent(agent) {
    this.interactiveAgent = agent;
  }

  managerNameForId(id) {
    const manager = _.find(this.employees, {id: String(id)});
    return _.get(manager, 'name');
  }

  @action openBulkEditModal(field) {
    this.bulkEditField = _.camelCase(field);
    this.bulkEditModalOpen = true;
    this.bulkEditValue = this.massUpdate.bulkEditValues.get(this.bulkEditField);
  }

  @action closeBulkEditModal() {
    this.bulkEditModalOpen = false;
  }

  @action openAddEmployeesModal() {
    this.addEmployeesModalOpen = true;
  }

  @action closeAddEmployeesModal() {
    this.addEmployeesModalOpen = false;
  }

  @action openPublishConfirmationModal() {
    this.publishConfirmationModalOpen = true;
  }

  @action closePublishConfirmationModal() {
    this.publishConfirmationModalOpen = false;
  }

  @action async load() {
    await this.store._compose([
      endpoints.MASS_UPDATES.with(this.match.params.id),
      endpoints.EMPLOYEES
    ]);

    this.massUpdate = new MassUpdate(this.store._getSingle(types.MASS_UPDATE));
    this.employees = this.store._getAll(types.EMPLOYEE, Employee);

    if (!this.massUpdate.readOnly) {
      await setupAutosaveDraft(
        this.massUpdate,
        {id: this.massUpdate.id, type: types.MASS_UPDATE}
      );
    }

    this.transformNewPayRates();
    this.massUpdate.newValues = observable.map(this.massUpdate.newValues);
    this.massUpdate.bulkEditValues = observable.map(this.massUpdate.bulkEditValues);
    Array.from(this.massUpdate.newValues.keys()).map((employeeId) => {
      return this.massUpdate.newValues.set(employeeId, observable.map(this.massUpdate.newValues.get(employeeId)));
    });
  }

  @action transformNewPayRates() {
    if (this.massUpdate.newPayRates) {
      Object.entries(this.massUpdate.newPayRates).forEach(([employeeId, payRate]) => {
        this.massUpdate.newValues[employeeId] = {payRate};
      });
      delete this.massUpdate.newPayRates;
    }
  }

  @action updateValue(value, model, field) {
    if (!this.massUpdate.newValues.has(model.id)) {
      this.massUpdate.newValues.set(model.id, observable.map({}));
    }
    this.massUpdate.newValues.get(model.id).set(field, value);
    this.massUpdate.autosaver.autosave();
  }

  @action updateMassUpdate(newValues) {
    this.massUpdate.merge(newValues);
    this.massUpdate.autosaver.autosave();
  }

  @action setBulkEditValue(value) {
    this.bulkEditValue = value;
  }

  @action async applyBulkEdit() {
    this.massUpdate.bulkEditValues.set(this.bulkEditField, this.bulkEditValue);
    this.massUpdate.newValues.forEach((values) => {
      values.delete(this.bulkEditField);
    });
    this.massUpdate.autosaver.autosave();
    this.closeBulkEditModal();
  }

  @action async removeMassUpdateEmployee(model) {
    await this.store.destroy(model);

    delete this.massUpdate.newValues[model.id];
    this.massUpdate.autosaver.autosave();
  }

  @action async addEmployeesToMassUpdate() {
    const selectedEmployeeIds = _.map(this.selectedEmployees, 'id');
    const {errors} = await this.store.post(
      this.massUpdate.link('createMassUpdateEmployees') + (this.massUpdate.effectiveDate ? `?effective_date=${this.massUpdate.effectiveDate}` : ''),
      types.MASS_UPDATE_EMPLOYEE,
      {employeeIds: selectedEmployeeIds}
    );

    this.store._getAll(
      types.MASS_UPDATE_EMPLOYEE,
      (update) => selectedEmployeeIds.includes(update.employee.id),
      MassUpdateEmployee
    ).forEach(update => {
      this.updateValue(update.employee.managerId, update, 'manager');
    });

    this.addEmployeeErrors = errors;

    if (_.isEmpty(errors)) {
      this.interactiveAgent.refresh();
      this.closeAddEmployeesModal();
      this.selectedEmployees = [];
    }
  }

  @action async publishMassUpdate() {
    const {model, errors} = await this.store.post(
      this.massUpdate.link('publish'),
      types.MASS_UPDATE,
      this.massUpdate
    );

    this.errors = errors;

    if (model) {
      this.history.push('/');
    }

    this.closePublishConfirmationModal();
  }

  @action updateIncludedField(field, checked) {
    this.massUpdate.toggleIncludedField(field, checked);
    this.massUpdate.autosaver.autosave();
  }
}

export default MassUpdateEditorState;
